import { doc, getDocFromServer, runTransaction, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import { writeTrainingWithGateway } from './writeGateway';
import { isValidUserId } from './model';
import { TRAINING_COLLECTION } from './store';
import { PUBLICATION_COLLECTION, TRAINING_ACCESS_COLLECTION, buildPublishedTraining, readPublishedTraining } from './publication';

const error = (code, message) => Object.assign(new Error(message), { code: `training-publication/${code}` });
const identity = () => {
  const user = auth.currentUser;
  if (!user || !isValidUserId(user.uid)) throw error('unauthenticated', 'Accedi per aprire i tuoi allenamenti.');
  return user;
};
const sameSession = user => {
  if (auth.currentUser !== user) throw error('unauthenticated', 'La sessione è cambiata. Accedi nuovamente.');
};
const checkTarget = userId => {
  if (!isValidUserId(userId)) throw error('identity', 'Il riferimento al cliente non è valido.');
};
const staffEnabled = (snapshot, uid) => snapshot.exists() && Array.isArray(snapshot.data()?.uids) && snapshot.data().uids.includes(uid);

async function verifyPublisher(user) {
  const [profile, staff] = await Promise.all([
    getDocFromServer(doc(db, 'users', user.uid)),
    getDocFromServer(doc(db, TRAINING_ACCESS_COLLECTION, '_staff')),
  ]);
  sameSession(user);
  if (!profile.exists() || !['Amministratore', 'Studio'].includes(profile.data()?.userRole) || !staffEnabled(staff, user.uid)) {
    throw error('forbidden', 'Il tuo account non è abilitato alla pubblicazione degli allenamenti.');
  }
}

export async function hasClientTrainingAccess() {
  const published = await loadPublishedTraining();
  return !!published.plan;
}

export async function loadPublishedTraining() {
  // The destination comes exclusively from Firebase Auth, never a URL or form.
  const user = identity();
  const snapshot = await getDocFromServer(doc(db, PUBLICATION_COLLECTION, user.uid));
  sameSession(user);
  if (!snapshot.exists()) return { plan: null, revision: 0, publishedAt: null };
  const published = readPublishedTraining(snapshot.data(), user.uid);
  return { plan: published.plan, revision: published.publicationRevision, publishedAt: published.publishedAt };
}

export async function loadTrainingPublicationStatus(userId) {
  checkTarget(userId);
  const user = identity();
  await verifyPublisher(user);
  const snapshot = await getDocFromServer(doc(db, PUBLICATION_COLLECTION, userId));
  sameSession(user);
  const published = snapshot.exists() ? readPublishedTraining(snapshot.data(), userId) : null;
  return {
    enabled: true,
    sourceRevision: published?.sourceRevision || 0,
    publicationRevision: published?.publicationRevision || 0,
    publishedAt: published?.publishedAt || null,
  };
}

export async function publishTraining(userId, expectedSourceRevision, expectedPublicationRevision) {
  checkTarget(userId);
  const user = identity();
  if (!Number.isSafeInteger(expectedSourceRevision) || expectedSourceRevision < 1 ||
      !Number.isSafeInteger(expectedPublicationRevision) || expectedPublicationRevision < 0 || expectedPublicationRevision >= Number.MAX_SAFE_INTEGER) {
    throw error('conflict', 'Salva e ricarica la scheda prima di pubblicarla.');
  }
  const remote = await writeTrainingWithGateway('publish', { userId, expectedRevision: expectedSourceRevision, expectedPublicationRevision });
  if (remote) return remote;
  const target = doc(db, PUBLICATION_COLLECTION, userId);
  const result = await runTransaction(db, async transaction => {
    const profile = await transaction.get(doc(db, 'users', user.uid));
    const staff = await transaction.get(doc(db, TRAINING_ACCESS_COLLECTION, '_staff'));
    const source = await transaction.get(doc(db, TRAINING_COLLECTION, userId));
    const current = await transaction.get(target);
    sameSession(user);
    if (!profile.exists() || !['Amministratore', 'Studio'].includes(profile.data()?.userRole) || !staffEnabled(staff, user.uid)) {
      throw error('forbidden', 'Il tuo account non è abilitato alla pubblicazione degli allenamenti.');
    }
    const prior = current.exists() ? readPublishedTraining(current.data(), userId) : null;
    if (!source.exists() || source.data()?.revision !== expectedSourceRevision ||
        (prior?.publicationRevision || 0) !== expectedPublicationRevision) {
      throw error('conflict', 'La scheda o la pubblicazione è cambiata in un’altra finestra. Ricarica prima di pubblicare.');
    }
    const publication = buildPublishedTraining(source.data(), {
      userId, sourceRevision: expectedSourceRevision,
      publicationRevision: expectedPublicationRevision + 1, publishedBy: user.uid,
    });
    transaction.set(target, { ...publication, publishedAt: serverTimestamp() });
    return {
      enabled: true, sourceRevision: publication.sourceRevision,
      publicationRevision: publication.publicationRevision,
    };
  });
  // Successful transaction acknowledgement is sufficient; no post-write read.
  return { ...result, publishedAt: new Date().toISOString() };
}

export function trainingPublicationErrorMessage(err) {
  if (err?.code?.startsWith('training-publication/') || err?.code?.startsWith('training/')) return err.message;
  const code = err?.code?.replace(/^firestore\//, '');
  if (code === 'permission-denied') return 'Accesso agli allenamenti non autorizzato. Contatta il team Fusco Fit.';
  if (['unavailable', 'deadline-exceeded'].includes(code)) return 'Connessione al server non disponibile. Riprova tra poco.';
  return 'Non è stato possibile caricare o pubblicare la scheda. Riprova.';
}
